<template>
  <div>
    <h3 v-if="title" class="text-center text-base text-gray-dark font-bold flex justify-center">
      {{ title }}

      <vs-tooltip v-if="hint" class="flex ml-1 items-center" position="bottom">
        <template #content>
          <div v-html="hint"/>
        </template>

        <vs-icon icon="info" class="text-gray cursor-default"/>
      </vs-tooltip>
    </h3>

    <with-loader class="w-full flex flex-col justify-between py-6 z-0"
                 :class="{ 'h-112': isLoading, 'h-full': !isLoading }"
                 :loading="isLoading"
                 :id="id"
                 use-as-overlay
    >
      <div v-if="!hasData || isLoading" class="w-full h-full flex justify-center items-center">
        <span v-if="!isLoading" class="font-bold text-gray text-6xl pb-16">–</span>
      </div>

      <template v-else>
        <!-- chart docs at https://apexcharts.com/docs/chart-types/pie-donut/ -->
        <vue-apex-charts type="pie"
                         :height="`${computedChartHeight}px`"
                         :series="series"
                         :options="options"
        />
        <div class="flex flex-wrap flex-1 justify-evenly self-center gap-x-5 gap-y-2 mt-6">
          <chart-legend v-for="({ legendType }, i) in chartData" :key="i" :type="legendType"/>
        </div>
      </template>

    </with-loader>
  </div>
</template>

<script>
import VueApexCharts from 'vue-apexcharts'
import tailwind from '../../../../tailwind.config'
import ChartLegend from '@/components/analytics/charts/ChartLegend'
import isEmpty from 'ramda/src/isEmpty'
import chartLabels from '@/utils/chart-labels'
import WithLoader from '@/components/common/WithLoader'

const themeColors = tailwind.theme.colors

export default {
  name: 'PieChart',

  components: { VueApexCharts, ChartLegend, WithLoader },

  props: {
    id: {
      type: String,
      required: true
    },
    aggregateRequestOutcomes: {
      type: Array,
      required: true
    },
    isLoading: {
      type: Boolean,
      required: true
    },
    total: {
      type: Number,
      required: true
    },
    defaultChartData: {
      type: Array,
      required: true
    },
    title: {
      type: String,
      default: ''
    },
    hint: {
      type: String,
      default: ''
    },
    chartHeight: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      windowWidth: window.innerWidth
    }
  },

  computed: {
    chartData() {
      return isEmpty(this.aggregateRequestOutcomes) ?
        this.defaultChartData :
        this.aggregateRequestOutcomes
    },
    hasData() {
      return !isEmpty(this.aggregateRequestOutcomes)
    },

    computedChartHeight() {
      if (this.chartHeight) return this.chartHeight
      if (this.windowWidth < 768) return this.windowWidth * 0.5

      const chartHeight = this.windowWidth * 0.36
      return chartHeight < 360 ? chartHeight : 360
    },

    series() {
      return this.chartData.map(chartData => chartData.count)
    },

    options() {
      return {
        labels: this.chartData.map(chartData => `${chartLabels[chartData.legendType].label} : <b>${(chartData.count / this.total * 100).toFixed(1)}%</b> `),
        colors: this.chartData.map(chartData => themeColors[chartLabels[chartData.legendType].color]),
        dataLabels: {
          enabled: true,
          style: {
            fontSize: '12px',
            fontFamily: 'Figtree, Arial, sans-serif',
            colors: ['#fff']
          },
          dropShadow: {
            enabled: true,
            blur: 5
          }
        },
        legend: {
          show: false // Difficult to style the provided legend + difficult to implement vs-tooltip to legend key. We're using a custom ChartLegend component.
        },
        plotOptions: {
          pie: {
            startAngle: -180,
            endAngle: 180,
            dataLabels: {
              minAngleToShowLabel: 0
            }
          }
        },
        states: {
          hover: {
            filter: {
              type: 'darken',
              value: 0.5
            }
          }
        }
      }
    }
  },

  methods: {
    setWindowWidth() {
      this.windowWidth = window.innerWidth
    }
  },

  mounted() {
    window.addEventListener('resize', this.setWindowWidth)
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.setWindowWidth)
  }
}
</script>
